import {Item, ItemType, Workspace} from '../../../../API';
import {TENANT_ID, WORKSPACE_ID, CLIENT_USER_ID, CLIENT_USER_ID_2, CLIENT_USER_ID_3, ROLE_ID_STORE_MANAGER} from '../fixtures';

// ── Stable IDs ──────────────────────────────────────────────────────────────
// First 6 chars of each ID must be unique (shortenId uses substring(0, 6)).
const LIBRARY_ID = 'lib001-0000-0000-0000-000000000040';

const SEC_OPS    = 'ops001-0000-0000-0000-000000000041';
const SEC_HR     = 'hr0001-0000-0000-0000-000000000042';
const SEC_INV    = 'inv001-0000-0000-0000-000000000043';
const SEC_SAFETY = 'saf001-0000-0000-0000-000000000044';

const LEAF_OPENING       = 'lf1001-0000-0000-0000-000000000051';
const LEAF_POS           = 'lf2001-0000-0000-0000-000000000052';
const LEAF_CUST_SVC      = 'lf3001-0000-0000-0000-000000000053';
const LEAF_ONBOARD       = 'lf4001-0000-0000-0000-000000000054';
const LEAF_SCHEDULING    = 'lf5001-0000-0000-0000-000000000055';
const LEAF_CONDUCT       = 'lf6001-0000-0000-0000-000000000056';
const LEAF_RECEIVING     = 'lf7001-0000-0000-0000-000000000057';
const LEAF_STOCKCOUNT    = 'lf8001-0000-0000-0000-000000000058';
const LEAF_RETURNS       = 'lf9001-0000-0000-0000-000000000059';
const LEAF_LOSS_PREV     = 'lfa001-0000-0000-0000-00000000005a';
const LEAF_VISUAL_MERCH  = 'lfb001-0000-0000-0000-00000000005b';
const LEAF_HEALTH_SAFETY = 'lfc001-0000-0000-0000-00000000005c';

// ── Task list IDs ────────────────────────────────────────────────────────────
export const TLU_DAILY     = 'tlu001-0000-0000-0000-000000000061';
export const TLU_INVENTORY = 'tlu002-0000-0000-0000-000000000062';

const TL_MORNING = 'tl1001-0000-0000-0000-000000000071';
const TL_EVENING = 'tl2001-0000-0000-0000-000000000072';
const TL_RESTOCK = 'tl3001-0000-0000-0000-000000000073';
const TL_SHELVES = 'tl4001-0000-0000-0000-000000000074';

const TASK_CLEAN_ENTRANCE  = 'ta1001-0000-0000-0000-000000000081';
const TASK_CASH_FLOAT      = 'ta2001-0000-0000-0000-000000000082';
const TASK_FLOOR_WALK      = 'ta3001-0000-0000-0000-000000000083';
const TASK_POS_ON          = 'ta4001-0000-0000-0000-000000000084';
const TASK_PHOTO_FLOOR     = 'ta5001-0000-0000-0000-000000000085';
const TASK_POS_REPORT      = 'ta6001-0000-0000-0000-000000000086';
const TASK_CASH_DROP       = 'ta7001-0000-0000-0000-000000000087';
const TASK_PHOTO_STOCKROOM = 'ta8001-0000-0000-0000-000000000088';
const TASK_CHECK_STOCK     = 'ta9001-0000-0000-0000-000000000089';
const TASK_PULL_STOCK      = 'taa001-0000-0000-0000-00000000008a';
const TASK_PHOTO_SHELVES   = 'tab001-0000-0000-0000-00000000008b';
const TASK_SHELF_AUDIT     = 'tac001-0000-0000-0000-00000000008c';
const TASK_PLANOGRAM       = 'tad001-0000-0000-0000-00000000008d';
// Referenced by retail-chain-reliability.ts as a resolved Issue's corrective task.
const TASK_ENTRANCE_SIGN   = 'tae001-0000-0000-0000-00000000008e';
const TASK_SEASONAL_TEARDOWN = 'taf001-0000-0000-0000-00000000008f';

const NOW = new Date('2026-01-01T00:00:00.000Z').toISOString();

// ── Item helpers ─────────────────────────────────────────────────────────────
function leaf(id: string, parentId: string, name: string, opts?: Partial<Item>): Item {
    return {
        __typename: 'Item',
        id,
        ownerId: TENANT_ID,
        workspaceId: WORKSPACE_ID,
        name,
        type: ItemType.LEAF,
        childIds: '[]',
        parentIds: JSON.stringify([parentId]),
        readRoles: '[]',
        writeRoles: '[]',
        creator: CLIENT_USER_ID,
        updatedBy: CLIENT_USER_ID,
        archived: false,
        trashed: false,
        pinned: false,
        pinnedIds: '[]',
        createdAt: NOW,
        updatedAt: NOW,
        version: 1,
        deletedAt: null,
        slug: null,
        description: null,
        acknowledgement: null,
        acknowledgementRequired: null,
        ackUpdatedBy: null,
        statusRestoreParentIds: null,
        statusSourceLibraryId: null,
        statusUpdatedBy: null,
        ...opts,
    };
}

function library(id: string, name: string, childIds: string[], slug: string): Item {
    return {
        __typename: 'Item',
        id,
        ownerId: TENANT_ID,
        workspaceId: WORKSPACE_ID,
        name,
        type: ItemType.LIBRARY,
        childIds: JSON.stringify(childIds),
        parentIds: JSON.stringify([WORKSPACE_ID]),
        readRoles: '[]',
        writeRoles: '[]',
        creator: CLIENT_USER_ID,
        updatedBy: CLIENT_USER_ID,
        archived: false,
        trashed: false,
        pinned: false,
        pinnedIds: '[]',
        createdAt: NOW,
        updatedAt: NOW,
        version: 1,
        deletedAt: null,
        slug,
        description: null,
        acknowledgement: null,
        acknowledgementRequired: null,
        ackUpdatedBy: null,
        statusRestoreParentIds: null,
        statusSourceLibraryId: null,
        statusUpdatedBy: null,
    };
}

function cluster(id: string, name: string, childIds: string[]): Item {
    return {
        __typename: 'Item',
        id,
        ownerId: TENANT_ID,
        workspaceId: WORKSPACE_ID,
        name,
        type: ItemType.CLUSTER,
        childIds: JSON.stringify(childIds),
        parentIds: JSON.stringify([LIBRARY_ID]),
        readRoles: '[]',
        writeRoles: '[]',
        creator: CLIENT_USER_ID,
        updatedBy: CLIENT_USER_ID,
        archived: false,
        trashed: false,
        pinned: false,
        pinnedIds: '[]',
        createdAt: NOW,
        updatedAt: NOW,
        version: 1,
        deletedAt: null,
        slug: null,
        description: null,
        acknowledgement: null,
        acknowledgementRequired: null,
        ackUpdatedBy: null,
        statusRestoreParentIds: null,
        statusSourceLibraryId: null,
        statusUpdatedBy: null,
    };
}

// ── Workspace users ──────────────────────────────────────────────────────────
// These feed listWsUsers → acknowledgment confirmed/unconfirmed display.
export const wsUsers = [
    {__typename: 'WorkspaceUser', id: 'wsu001-0000-0000-0000-000000000091', ownerId: TENANT_ID, workspaceId: WORKSPACE_ID, userId: CLIENT_USER_ID,  role: 'admin',  version: 1, createdAt: NOW, updatedAt: NOW, deletedAt: null},
    {__typename: 'WorkspaceUser', id: 'wsu002-0000-0000-0000-000000000092', ownerId: TENANT_ID, workspaceId: WORKSPACE_ID, userId: CLIENT_USER_ID_2, role: 'member', version: 1, createdAt: NOW, updatedAt: NOW, deletedAt: null},
    {__typename: 'WorkspaceUser', id: 'wsu003-0000-0000-0000-000000000093', ownerId: TENANT_ID, workspaceId: WORKSPACE_ID, userId: CLIENT_USER_ID_3, role: 'member', version: 1, createdAt: NOW, updatedAt: NOW, deletedAt: null},
];

// ── Items ────────────────────────────────────────────────────────────────────
export const items: Item[] = [
    library(LIBRARY_ID, 'Main Library', [SEC_OPS, SEC_HR, SEC_INV, SEC_SAFETY], 'main'),

    cluster(SEC_OPS,    'Store Operations',       [LEAF_OPENING, LEAF_POS, LEAF_CUST_SVC]),
    cluster(SEC_HR,     'People & HR',            [LEAF_ONBOARD, LEAF_SCHEDULING, LEAF_CONDUCT]),
    cluster(SEC_INV,    'Inventory',              [LEAF_RECEIVING, LEAF_STOCKCOUNT, LEAF_RETURNS]),
    cluster(SEC_SAFETY, 'Standards & Safety',     [LEAF_LOSS_PREV, LEAF_VISUAL_MERCH, LEAF_HEALTH_SAFETY]),

    leaf(LEAF_OPENING,    SEC_OPS, 'Store Opening & Closing Procedure'),
    leaf(LEAF_POS,        SEC_OPS, 'POS & Cash Handling'),
    leaf(LEAF_CUST_SVC,   SEC_OPS, 'Customer Service Standards'),

    leaf(LEAF_ONBOARD,    SEC_HR, 'New Employee Onboarding'),
    leaf(LEAF_SCHEDULING, SEC_HR, 'Shift Scheduling & Time-Off'),
    // Code of Conduct: required by manager; manager + Alex confirmed, Sam has not yet.
    leaf(LEAF_CONDUCT, SEC_HR, 'Code of Conduct', {
        acknowledgementRequired: true,
        acknowledgement: JSON.stringify({
            [CLIENT_USER_ID]:  {date: NOW},
            [CLIENT_USER_ID_2]: {date: NOW},
        }),
        ackUpdatedBy: CLIENT_USER_ID,
    }),

    leaf(LEAF_RECEIVING,  SEC_INV, 'Receiving & Tagging Goods'),
    leaf(LEAF_STOCKCOUNT, SEC_INV, 'Stock Count Procedure'),
    leaf(LEAF_RETURNS,    SEC_INV, 'Returns & Exchanges Policy'),

    // Loss Prevention: required; only manager confirmed so far.
    leaf(LEAF_LOSS_PREV, SEC_SAFETY, 'Loss Prevention Guidelines', {
        acknowledgementRequired: true,
        acknowledgement: JSON.stringify({
            [CLIENT_USER_ID]: {date: NOW},
        }),
        ackUpdatedBy: CLIENT_USER_ID,
    }),
    leaf(LEAF_VISUAL_MERCH,  SEC_SAFETY, 'Visual Merchandising Standards'),
    leaf(LEAF_HEALTH_SAFETY, SEC_SAFETY, 'Health, Safety & Emergency Procedures'),
];

// ── Workspace override ────────────────────────────────────────────────────────
export const workspaceOverride: Partial<Workspace> = {
    childIds: JSON.stringify([LIBRARY_ID]),
    name: 'Meridian Retail Co.',
};

// ── Task list units ───────────────────────────────────────────────────────────
export const taskListUnits = [
    {
        __typename: 'TaskListUnit',
        id: TLU_DAILY,
        ownerId: TENANT_ID,
        workspaceId: WORKSPACE_ID,
        name: 'Store Daily Checklist',
        description: 'Opening and closing tasks completed every shift by the responsible team member.',
        timezone: 'America/Chicago',
        logicalDayShift: 4,
        avatarPath: null,
        isArchived: false,
        createdBy: CLIENT_USER_ID,
        updatedBy: CLIENT_USER_ID,
        version: 1,
        createdAt: NOW,
        updatedAt: NOW,
        taskListsOrder: [TL_MORNING, TL_EVENING],
        taskLists: {
            [TL_MORNING]: {
                id: TL_MORNING,
                name: 'Morning Opening',
                description: 'Complete before doors open.',
                childIds: [TASK_CLEAN_ENTRANCE, TASK_ENTRANCE_SIGN, TASK_CASH_FLOAT, TASK_FLOOR_WALK, TASK_POS_ON, TASK_PHOTO_FLOOR],
                isArchived: false, createdAt: NOW, updatedAt: NOW, createdBy: CLIENT_USER_ID, updatedBy: CLIENT_USER_ID,
            },
            [TL_EVENING]: {
                id: TL_EVENING,
                name: 'Evening Closing',
                description: 'Complete before securing the store.',
                childIds: [TASK_POS_REPORT, TASK_CASH_DROP, TASK_PHOTO_STOCKROOM],
                isArchived: false, createdAt: NOW, updatedAt: NOW, createdBy: CLIENT_USER_ID, updatedBy: CLIENT_USER_ID,
            },
        },
    },
    {
        __typename: 'TaskListUnit',
        id: TLU_INVENTORY,
        ownerId: TENANT_ID,
        workspaceId: WORKSPACE_ID,
        name: 'Inventory & Merchandising',
        description: 'Daily restock and weekly shelf-standard checks.',
        timezone: 'America/Chicago',
        logicalDayShift: 4,
        avatarPath: null,
        isArchived: false,
        createdBy: CLIENT_USER_ID,
        updatedBy: CLIENT_USER_ID,
        version: 1,
        createdAt: NOW,
        updatedAt: NOW,
        taskListsOrder: [TL_RESTOCK, TL_SHELVES],
        taskLists: {
            [TL_RESTOCK]: {
                id: TL_RESTOCK,
                name: 'Daily Restock',
                description: 'Pull from backroom and replenish floor stock.',
                childIds: [TASK_CHECK_STOCK, TASK_PULL_STOCK, TASK_PHOTO_SHELVES],
                isArchived: false, createdAt: NOW, updatedAt: NOW, createdBy: CLIENT_USER_ID, updatedBy: CLIENT_USER_ID,
            },
            [TL_SHELVES]: {
                id: TL_SHELVES,
                name: 'Weekly Shelf Audit',
                description: 'Monday morning planogram check before trading opens.',
                childIds: [TASK_SHELF_AUDIT, TASK_PLANOGRAM],
                isArchived: false, createdAt: NOW, updatedAt: NOW, createdBy: CLIENT_USER_ID, updatedBy: CLIENT_USER_ID,
            },
        },
    },
];

// ── Tasks ─────────────────────────────────────────────────────────────────────
const dailyRule = {startDate: '2026-01-01', repeatOption: 'every-day'};
const weeklyRule = {startDate: '2026-01-05', repeatOption: 'every-week', weekDays: [1]};
// One-time schedule whose single due date is long past "today" - demo example
// of the schedule-ended row marker (isTaskScheduleEnded), so it has a real
// fixture to check against the same way TASK_ENTRANCE_SIGN/TASK_SHELF_AUDIT
// do for the corrective marker.
const endedOneTimeRule = {startDate: '2026-02-14', endDate: '2026-02-14', repeatOption: 'one-time'};

export const tasks = [
    // Morning opening
    {__typename: 'Task', id: TASK_CLEAN_ENTRANCE, taskListUnitId: TLU_DAILY, taskListId: TL_MORNING, name: 'Clean entrance area and wipe door glass', description: '', scheduleRule: dailyRule, relatedPage: null, config: null, images: null, isArchived: false, version: 1, createdAt: NOW, updatedAt: NOW, createdBy: CLIENT_USER_ID, updatedBy: CLIENT_USER_ID},
    {__typename: 'Task', id: TASK_CASH_FLOAT, taskListUnitId: TLU_DAILY, taskListId: TL_MORNING, name: 'Count and record opening cash float ($300 target)', description: 'Variance over $5 must be noted in the opening log.', scheduleRule: dailyRule, relatedPage: LEAF_POS, config: null, images: null, isArchived: false, version: 1, createdAt: NOW, updatedAt: NOW, createdBy: CLIENT_USER_ID, updatedBy: CLIENT_USER_ID},
    {__typename: 'Task', id: TASK_FLOOR_WALK, taskListUnitId: TLU_DAILY, taskListId: TL_MORNING, name: 'Walk the floor — note any damage, missing stock, or hazard', description: 'Report anything requiring action before opening.', scheduleRule: dailyRule, relatedPage: LEAF_OPENING, config: null, images: null, isArchived: false, version: 1, createdAt: NOW, updatedAt: NOW, createdBy: CLIENT_USER_ID, updatedBy: CLIENT_USER_ID},
    {__typename: 'Task', id: TASK_POS_ON, taskListUnitId: TLU_DAILY, taskListId: TL_MORNING, name: 'Power on all POS terminals and confirm today\'s schedule', description: '', scheduleRule: dailyRule, relatedPage: null, config: null, images: null, isArchived: false, version: 1, createdAt: NOW, updatedAt: NOW, createdBy: CLIENT_USER_ID, updatedBy: CLIENT_USER_ID},
    {__typename: 'Task', id: TASK_PHOTO_FLOOR, taskListUnitId: TLU_DAILY, taskListId: TL_MORNING, name: 'Photo report: main floor displays before opening', description: 'Capture the full floor from the entrance. Photo is mandatory.', scheduleRule: dailyRule, relatedPage: LEAF_VISUAL_MERCH, config: {isImageRequired: true}, images: null, isArchived: false, version: 1, createdAt: NOW, updatedAt: NOW, createdBy: CLIENT_USER_ID, updatedBy: CLIENT_USER_ID},
    {__typename: 'Task', id: TASK_ENTRANCE_SIGN, taskListUnitId: TLU_DAILY, taskListId: TL_MORNING, name: 'Replace entrance sign', description: 'New wet-floor sign for the entrance — old one was left out overnight after a spill.', scheduleRule: dailyRule, relatedPage: null, config: null, images: null, isArchived: false, version: 1, createdAt: NOW, updatedAt: NOW, createdBy: CLIENT_USER_ID, updatedBy: CLIENT_USER_ID,
        // Demo example of an Issue resolved via a corrective task (retail-chain-reliability.ts iss005).
        isCorrective: true, linkedIssueId: 'iss005-0000-0000-0000-0000000000e5', correctiveReason: 'Fixes the "Spill near entrance left without a wet-floor sign" issue.'},

    // Evening closing
    {__typename: 'Task', id: TASK_POS_REPORT, taskListUnitId: TLU_DAILY, taskListId: TL_EVENING, name: 'Run end-of-day report on all POS terminals', description: 'Print a copy and place in the manager\'s binder.', scheduleRule: dailyRule, relatedPage: LEAF_POS, config: null, images: null, isArchived: false, version: 1, createdAt: NOW, updatedAt: NOW, createdBy: CLIENT_USER_ID, updatedBy: CLIENT_USER_ID},
    {__typename: 'Task', id: TASK_CASH_DROP, taskListUnitId: TLU_DAILY, taskListId: TL_EVENING, name: 'Cash count and safe drop (leave $300 float)', description: 'Both team members must sign the cash-up form.', scheduleRule: dailyRule, relatedPage: LEAF_POS, config: null, images: null, isArchived: false, version: 1, createdAt: NOW, updatedAt: NOW, createdBy: CLIENT_USER_ID, updatedBy: CLIENT_USER_ID},
    {__typename: 'Task', id: TASK_PHOTO_STOCKROOM, taskListUnitId: TLU_DAILY, taskListId: TL_EVENING, name: 'Photo report: stockroom tidied for next morning', description: 'Shows all stock is shelved and pathways are clear.', scheduleRule: dailyRule, relatedPage: LEAF_RECEIVING, config: {isImageRequired: true}, images: null, isArchived: false, version: 1, createdAt: NOW, updatedAt: NOW, createdBy: CLIENT_USER_ID, updatedBy: CLIENT_USER_ID},

    // Daily restock
    {__typename: 'Task', id: TASK_CHECK_STOCK, taskListUnitId: TLU_INVENTORY, taskListId: TL_RESTOCK, name: 'Check low-stock alerts in the inventory system', description: '', scheduleRule: dailyRule, relatedPage: LEAF_STOCKCOUNT, config: null, images: null, isArchived: false, version: 1, createdAt: NOW, updatedAt: NOW, createdBy: CLIENT_USER_ID, updatedBy: CLIENT_USER_ID},
    {__typename: 'Task', id: TASK_PULL_STOCK, taskListUnitId: TLU_INVENTORY, taskListId: TL_RESTOCK, name: 'Pull flagged stock from backroom and shelve correctly', description: 'Follow the planogram for display locations.', scheduleRule: dailyRule, relatedPage: LEAF_RECEIVING, config: null, images: null, isArchived: false, version: 1, createdAt: NOW, updatedAt: NOW, createdBy: CLIENT_USER_ID, updatedBy: CLIENT_USER_ID},
    {__typename: 'Task', id: TASK_PHOTO_SHELVES, taskListUnitId: TLU_INVENTORY, taskListId: TL_RESTOCK, name: 'Photo report: restocked shelves after pull', description: 'Capture each restocked section. Photo is mandatory.', scheduleRule: dailyRule, relatedPage: LEAF_VISUAL_MERCH, config: {isImageRequired: true}, images: null, isArchived: false, version: 1, createdAt: NOW, updatedAt: NOW, createdBy: CLIENT_USER_ID, updatedBy: CLIENT_USER_ID},

    // Weekly shelf audit
    {__typename: 'Task', id: TASK_SHELF_AUDIT, taskListUnitId: TLU_INVENTORY, taskListId: TL_SHELVES, name: 'Walk all sections — note any misplaced or mislabelled stock', description: '', scheduleRule: weeklyRule, relatedPage: LEAF_VISUAL_MERCH, config: null, images: null, isArchived: false, version: 1, createdAt: NOW, updatedAt: NOW, createdBy: CLIENT_USER_ID, updatedBy: CLIENT_USER_ID,
        // Demo example of a corrective task, so the row-level yellow marker has a
        // real example to check against (no fixture had one before).
        // Linked to the resolved iss011 audit finding in retail-chain-reliability.ts.
        isCorrective: true, linkedIssueId: 'iss011-0000-0000-0000-0000000000eb', correctiveReason: 'Raised to fix a recurring mislabelled-stock issue flagged by the Harbor Point audit.'},
    {__typename: 'Task', id: TASK_PLANOGRAM, taskListUnitId: TLU_INVENTORY, taskListId: TL_SHELVES, name: 'Confirm planogram compliance and fix any deviations', description: 'Reference the current planogram in Visual Merchandising Standards.', scheduleRule: weeklyRule, relatedPage: LEAF_VISUAL_MERCH, config: null, images: null, isArchived: false, version: 1, createdAt: NOW, updatedAt: NOW, createdBy: CLIENT_USER_ID, updatedBy: CLIENT_USER_ID},
    {__typename: 'Task', id: TASK_SEASONAL_TEARDOWN, taskListUnitId: TLU_INVENTORY, taskListId: TL_SHELVES, name: 'Take down Valentine\'s Day promo display', description: 'One-off teardown for the seasonal endcap - never got done, and the task was left scheduled instead of archived.', scheduleRule: endedOneTimeRule, relatedPage: LEAF_VISUAL_MERCH, config: null, images: null, isArchived: false, version: 1, createdAt: NOW, updatedAt: NOW, createdBy: CLIENT_USER_ID, updatedBy: CLIENT_USER_ID},
];

// ── ProseMirror document content ──────────────────────────────────────────────
export const docContents: Record<string, any> = {
    [LEAF_OPENING]: {
        type: 'doc',
        content: [
            {type: 'paragraph', content: [{type: 'text', text: 'All 8 Meridian locations follow the same daily procedure. The store manager on duty is responsible for completing and signing the checklist in the operations binder.'}]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Opening (60 minutes before doors open)'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Disarm the security system and sign the entry log with time and name.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Walk the sales floor and stockroom. Note any damage, missing stock, or safety hazard and report to district manager via Slack before opening.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Power on all POS terminals. Log in to the back-office system and confirm the day\'s schedule for all 21 staff across locations.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Check the cash drawer against yesterday\'s closing float — target is $300. Discrepancies over $5 must be recorded on the variance sheet.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Brief the opening team (2–3 staff depending on location size) on daily targets and any promotions.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Closing (last 30 minutes of trading)'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Announce closing 15 minutes before to give customers time to finish. No new customers after the closing announcement.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Run the end-of-day report on all POS terminals. Print a copy and place it in the manager\'s binder.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Count the cash drawer. Complete the cash-up form and seal the deposit bag. Drop in the safe — never leave cash in the drawer overnight.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Walk the stockroom to confirm all deliveries received today are tagged and shelved. See Receiving & Tagging Goods if any are pending.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Arm the security system. Record closing time and your name in the security log.'}]}]},
            ]},
        ],
    },
    [LEAF_POS]: {
        type: 'doc',
        content: [
            {type: 'paragraph', content: [{type: 'text', text: 'Every Meridian store runs on the same POS system. Cash handling errors are the leading cause of end-of-day discrepancies across our 8 locations — follow this guide exactly.'}]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Processing a sale'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Scan each item before quoting a price. Never manually enter a price unless the barcode is damaged — in that case, call a manager.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Always confirm the total with the customer before taking payment.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'For cash: count the change back to the customer. Place received notes under the tray until the transaction is complete.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'For card: never handle the customer\'s card. Direct them to tap, insert, or swipe on the reader.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Voids and refunds'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Voids require manager approval. Call the manager to the register — do not attempt to void without authorisation.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Refunds to the original payment method only. See Returns & Exchanges Policy for the full process.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'All voids and refunds are logged automatically by the POS. A summary is included in the end-of-day report.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'End-of-day cash-up'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Count the drawer with a second staff member present. Both sign the cash-up form.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Variances over $10 must be reported to the district manager the same evening. Do not leave it for the morning.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Leave $300 as the opening float. Bag the remainder and drop it in the safe.'}]}]},
            ]},
        ],
    },
    [LEAF_CUST_SVC]: {
        type: 'doc',
        content: [
            {type: 'paragraph', content: [{type: 'text', text: 'Meridian\'s reputation across all 8 stores rests on consistent, friendly service. These standards apply to every team member on the floor.'}]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'On the floor'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Acknowledge every customer within 30 seconds of them entering your zone — a nod and a smile if you\'re occupied, a greeting if you\'re free.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Ask open questions: "What brings you in today?" not "Can I help you?" (customers say no to yes/no questions).'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Never leave a customer mid-conversation to answer a phone or attend to a colleague\'s question. Signal you need a moment and finish with the customer first.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Handling complaints'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Listen fully before responding. Do not interrupt, even if you already know the answer.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Acknowledge the inconvenience: "I understand that\'s frustrating" goes a long way before you explain next steps.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'If you cannot resolve a complaint on the spot, call the manager — do not promise outcomes you\'re not authorised to deliver.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Log every complaint in the customer feedback form at the register. District managers review these weekly.'}]}]},
            ]},
        ],
    },
    [LEAF_ONBOARD]: {
        type: 'doc',
        content: [
            {type: 'paragraph', content: [{type: 'text', text: 'Welcome to Meridian Retail Co. We\'re a team of 21 across 8 stores. This guide covers everything you need for your first two weeks.'}]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Before day one'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'HR will email your employment contract and tax forms. Return signed copies before your start date.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'You\'ll receive a Welcome Pack with your store assignment, uniform size confirmation form, and first-week schedule.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Your store manager will send a Slack invite. Join the #all-stores and your store\'s private channel before day one.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'First week'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Day 1–2: shadow an experienced team member through a full shift. Do not operate the POS alone until you\'ve completed the POS training module.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Day 3: complete the POS & Cash Handling training with your manager. You\'ll be signed off to operate the register from Day 4.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Day 4–5: floor shifts with a buddy. Ask questions freely — your buddy is allocated specifically to support you this week.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'End of probation (90 days)'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'You\'ll have a 90-day check-in with your manager. Bring any questions about your role, pay, or schedule.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'After probation, you\'re eligible for the cross-training programme — you can request shifts at other Meridian locations.'}]}]},
            ]},
        ],
    },
    [LEAF_SCHEDULING]: {
        type: 'doc',
        content: [
            {type: 'paragraph', content: [{type: 'text', text: 'Schedules are managed centrally for all 8 stores by the district manager, with input from individual store managers. The roster for the coming week is published every Wednesday by 5 PM.'}]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Reading your schedule'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Schedules are posted in the #schedule channel on Slack. You\'ll get a direct message when your shifts are confirmed.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Check your assigned store — during busy periods you may be rostered at a different Meridian location. Travel time is not paid unless the shift is more than 30 minutes from your home store.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Requesting time off'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Submit time-off requests at least 14 days in advance via the HR portal. Requests under 14 days are not guaranteed.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Blackout dates (peak trading weeks) are published in January for the full year. Time off during blackout dates requires district manager approval.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Annual leave entitlement: 20 days per year (pro-rated for part-time). Carry-over limit is 5 days — unused leave beyond that is forfeited on 31 December.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Shift swaps'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Find your own swap partner from the same store and skill level. Post in #shift-swaps on Slack.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Both parties must confirm the swap in the HR portal. Your manager\'s approval is required before it takes effect.'}]}]},
            ]},
        ],
    },
    [LEAF_CONDUCT]: {
        type: 'doc',
        content: [
            {type: 'paragraph', content: [{type: 'text', text: 'Meridian Retail Co. expects every team member — all 21 of us, across all 8 stores — to treat colleagues, customers, and company property with respect. All staff must confirm they have read this policy.'}]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'In the workplace'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Arrive on time and in uniform. If you\'re running late, notify your store manager before your shift starts — not after.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Personal phone use is for breaks only. Keep your phone on silent and out of sight on the floor.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Do not discuss salary, disciplinary matters, or personnel decisions with colleagues on the floor or in customer areas.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Company property & data'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Your POS login is personal — do not share it. If you leave your terminal unattended, lock it.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Do not take photos of stock, pricing, or back-office screens and share externally. Competitor price-matching data is confidential.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Reporting concerns'}]},
            {type: 'paragraph', content: [{type: 'text', text: 'If you witness a conduct breach — by a colleague, customer, or supplier — report it to your store manager or directly to HR at hr@meridianretail.co. Reports can be anonymous. Retaliation against anyone who raises a concern in good faith is a serious breach of this policy.'}]},
        ],
    },
    [LEAF_RECEIVING]: {
        type: 'doc',
        content: [
            {type: 'paragraph', content: [{type: 'text', text: 'Deliveries arrive at all 8 Meridian stores on Tuesday and Friday mornings. The store manager or nominated keyholder must be present for every delivery.'}]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Accepting a delivery'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Count the cartons against the delivery note before the driver leaves. Note any discrepancy on the delivery note and have the driver sign it.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Inspect cartons for visible damage. Photograph any damage and email to receiving@meridianretail.co immediately.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Bring cartons to the stockroom. Do not leave them on the shop floor during trading hours.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Tagging & shelving'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Scan each item into the inventory system before attaching a price tag. Items on the floor without a system entry are a shrinkage risk.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Price tags must be attached to the inside rear label — never to the front of a garment or outer packaging.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Rotate stock: new stock goes to the back, existing stock moves forward. Check the planogram for correct display locations.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'All tagging must be completed within 4 hours of delivery. Unprocessed stock left overnight is a loss-prevention flag.'}]}]},
            ]},
        ],
    },
    [LEAF_STOCKCOUNT]: {
        type: 'doc',
        content: [
            {type: 'paragraph', content: [{type: 'text', text: 'Full stock counts happen quarterly across all 8 Meridian locations, always on the first Monday of the quarter before the store opens. Spot counts happen monthly on a rolling basis.'}]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Preparing for a count'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Freeze stock movements from close of business the day before. Do not receive or return any stock until the count is complete.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Print the count sheets from the inventory system. Assign each team member a section — no section should be counted by one person alone.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Counting'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Count each item twice independently. If counts differ, count a third time — the majority result stands.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Variances of more than 2% in any section must be recounted before the sheet is submitted.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'After the count'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Enter results in the inventory system within 2 hours of completing the count. The system will generate a variance report automatically.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Email the variance report to the district manager with a brief note on any significant discrepancies and likely causes.'}]}]},
            ]},
        ],
    },
    [LEAF_RETURNS]: {
        type: 'doc',
        content: [
            {type: 'paragraph', content: [{type: 'text', text: 'Meridian accepts returns and exchanges at any of our 8 stores, regardless of which location the purchase was made at.'}]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'What we accept'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Items in original condition with tags attached, within 30 days of purchase.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Proof of purchase required: receipt, order confirmation email, or bank statement showing the Meridian transaction.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Faulty items can be returned at any time within the statutory warranty period regardless of condition.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Processing a return at the register'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Open the Returns screen on the POS and scan the item barcode. The system will pull the original purchase details.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Refund to the original payment method only. Cash refunds for card purchases are not permitted under any circumstances.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'For exchanges: process the return first, then ring up the new item as a fresh sale. Do not try to swap on one transaction.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Returned items go in the designated returns bin in the stockroom — do not put them back on the floor until a manager has inspected them.'}]}]},
            ]},
        ],
    },
    [LEAF_LOSS_PREV]: {
        type: 'doc',
        content: [
            {type: 'paragraph', content: [{type: 'text', text: 'Shrinkage across our 8 stores costs Meridian significantly each year. Every team member has a role in preventing loss — from shoplifting to internal error. All staff are required to confirm they have read and understood this policy.'}]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Shoplifting — what to do'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Do not physically confront or restrain a suspected shoplifter. Your safety is the priority — items are insured, people are not.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Use the code word "Price check on aisle 3" over the radio to alert other staff without alarming the customer.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Note a description of the person, items involved, and the time. Submit a loss-prevention report to your manager the same shift.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'If you witness a theft in progress, call the manager immediately. The manager will decide whether to involve police.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Internal controls'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Never allow a colleague to override a price or void a transaction without manager sign-off. Process shortcuts create audit gaps.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Cash-up must always be done with two people. A solo cash count is a policy breach and will be flagged on audit.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'High-value items must be kept in locked cases. Keys are held by the store manager and one nominated keyholder per shift only.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Receiving and stockroom'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'All deliveries must be checked in and entered in the inventory system before stock goes to the floor. Unsealed cartons left overnight are a loss risk.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'The stockroom door must remain locked during trading hours. No customers or non-authorised personnel may enter.'}]}]},
            ]},
        ],
    },
    [LEAF_VISUAL_MERCH]: {
        type: 'doc',
        content: [
            {type: 'paragraph', content: [{type: 'text', text: 'Consistent, well-executed displays drive sales and reinforce the Meridian brand across all 8 locations. These standards apply to every floor layout change, new stock placement, and daily tidy.'}]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'The planogram'}]},
            {type: 'paragraph', content: [{type: 'text', text: 'A current planogram is pinned in the stockroom at all times. The district manager distributes an updated version at the start of each promotional period. Any deviation from the planogram must be approved by the store manager.'}]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Daily standards'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Shelves must be full to the face. A gap in a shelf is a missed sale — pull stock forward and restock from the back if needed.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Price tickets must be visible and match the POS system. A price mismatch at the register is a customer service failure and a potential legal issue.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Promotional signage goes up the morning of the promotion start date. Do not display signs early or leave them up after the promotion ends.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Photo reporting'}]},
            {type: 'paragraph', content: [{type: 'text', text: 'Morning and evening photo reports are required via the Daily Checklist. The district manager reviews photo reports each week. If a section fails the standard, you\'ll receive direct feedback within 24 hours.'}]},
        ],
    },
    [LEAF_HEALTH_SAFETY]: {
        type: 'doc',
        content: [
            {type: 'paragraph', content: [{type: 'text', text: 'All 8 Meridian stores are required to comply with local occupational health and safety regulations. Every team member must know the emergency exits and first-aid locations at their assigned store.'}]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Fire & evacuation'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'On hearing the alarm: stop what you\'re doing, do not collect personal belongings, and escort any customers near you to the nearest exit.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'The assembly point is marked on the floor map in the stockroom. Do not re-enter the store until the all-clear is given by the fire warden.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Monthly fire drills are conducted at each location. Participation is mandatory for all on-shift staff.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Slips, trips & hazards'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Any spill must be cleaned immediately or a "wet floor" sign placed within 60 seconds of discovery.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Boxes and stock must not block aisles or fire exits at any time.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Report any hazard — broken fittings, exposed wiring, cracked flooring — to the manager immediately and log it in the hazard report book.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'First aid'}]},
            {type: 'paragraph', content: [{type: 'text', text: 'The first-aid kit is located in the manager\'s office at each store. One team member per shift must hold a current first-aid certificate — check the schedule to see who is designated each day. For serious incidents, call emergency services first, then notify the district manager.'}]},
        ],
    },
};

// Inline SVG placeholder for the pre-seeded photo report — fully offline, no
// server file needed. ImageGalleryPreview falls through to `src` when `thumbPath`
// is absent, so this renders directly in the report modal.
const DEMO_FLOOR_IMG = `data:image/svg+xml,${encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 480">' +
    '<rect width="800" height="220" fill="#f0ece4"/>' +
    '<rect y="220" width="800" height="260" fill="#d8d0c8"/>' +
    '<line x1="0" y1="295" x2="800" y2="295" stroke="#c9c1b9" stroke-width="1"/>' +
    '<line x1="0" y1="370" x2="800" y2="370" stroke="#c9c1b9" stroke-width="1"/>' +
    '<rect x="20" y="48" width="210" height="16" fill="#7a6248" rx="2"/>' +
    '<rect x="20" y="96" width="210" height="16" fill="#7a6248" rx="2"/>' +
    '<rect x="20" y="144" width="210" height="16" fill="#7a6248" rx="2"/>' +
    '<rect x="20" y="192" width="210" height="16" fill="#7a6248" rx="2"/>' +
    '<rect x="30" y="30" width="22" height="18" fill="#c0392b" rx="2"/>' +
    '<rect x="56" y="32" width="22" height="16" fill="#2980b9" rx="2"/>' +
    '<rect x="82" y="28" width="22" height="20" fill="#27ae60" rx="2"/>' +
    '<rect x="108" y="30" width="22" height="18" fill="#d35400" rx="2"/>' +
    '<rect x="134" y="31" width="22" height="17" fill="#8e44ad" rx="2"/>' +
    '<rect x="160" y="29" width="22" height="19" fill="#16a085" rx="2"/>' +
    '<rect x="30" y="78" width="22" height="18" fill="#2980b9" rx="2"/>' +
    '<rect x="56" y="80" width="22" height="16" fill="#c0392b" rx="2"/>' +
    '<rect x="82" y="76" width="22" height="20" fill="#d35400" rx="2"/>' +
    '<rect x="108" y="79" width="22" height="17" fill="#27ae60" rx="2"/>' +
    '<rect x="134" y="77" width="22" height="19" fill="#16a085" rx="2"/>' +
    '<rect x="570" y="48" width="210" height="16" fill="#7a6248" rx="2"/>' +
    '<rect x="570" y="96" width="210" height="16" fill="#7a6248" rx="2"/>' +
    '<rect x="570" y="144" width="210" height="16" fill="#7a6248" rx="2"/>' +
    '<rect x="570" y="192" width="210" height="16" fill="#7a6248" rx="2"/>' +
    '<rect x="580" y="30" width="22" height="18" fill="#16a085" rx="2"/>' +
    '<rect x="606" y="32" width="22" height="16" fill="#d35400" rx="2"/>' +
    '<rect x="632" y="28" width="22" height="20" fill="#c0392b" rx="2"/>' +
    '<rect x="658" y="30" width="22" height="18" fill="#2980b9" rx="2"/>' +
    '<rect x="684" y="31" width="22" height="17" fill="#27ae60" rx="2"/>' +
    '<rect x="710" y="29" width="22" height="19" fill="#8e44ad" rx="2"/>' +
    '<rect x="580" y="78" width="22" height="18" fill="#8e44ad" rx="2"/>' +
    '<rect x="606" y="80" width="22" height="16" fill="#16a085" rx="2"/>' +
    '<rect x="632" y="76" width="22" height="20" fill="#d35400" rx="2"/>' +
    '<rect x="658" y="79" width="22" height="17" fill="#c0392b" rx="2"/>' +
    '<rect x="684" y="77" width="22" height="19" fill="#2980b9" rx="2"/>' +
    '<rect x="308" y="132" width="184" height="88" fill="#9e8e7e" rx="4"/>' +
    '<rect x="318" y="106" width="164" height="26" fill="#b0a090" rx="3"/>' +
    '<rect x="326" y="82" width="26" height="24" fill="#c0392b" rx="2"/>' +
    '<rect x="360" y="84" width="26" height="22" fill="#2980b9" rx="2"/>' +
    '<rect x="394" y="80" width="26" height="26" fill="#27ae60" rx="2"/>' +
    '<rect x="428" y="82" width="26" height="24" fill="#d35400" rx="2"/>' +
    '<rect x="462" y="83" width="26" height="23" fill="#8e44ad" rx="2"/>' +
    '<rect x="242" y="12" width="316" height="36" fill="#2c3e50" rx="4"/>' +
    '<text x="400" y="35" font-family="Arial,sans-serif" font-size="13" fill="#ecf0f1" text-anchor="middle" font-weight="bold">MERIDIAN RETAIL CO.</text>' +
    '<circle cx="250" cy="30" r="4" fill="#f39c12"/>' +
    '<circle cx="550" cy="30" r="4" fill="#f39c12"/>' +
    '<rect y="455" width="800" height="25" fill="#00000060"/>' +
    '<text x="400" y="471" font-family="Arial,sans-serif" font-size="11" fill="#fff" text-anchor="middle">Morning opening photo \xb7 Main Floor \xb7 Jun 24, 2026 \xb7 08:45</text>' +
    '</svg>'
)}`;

// Pre-seeded completed task reports — gives the demo a realistic history without
// requiring staff to complete tasks during each demo session.
export const taskReports = [
    {
        __typename: 'TaskReport' as const,
        id: 'rpt001-0000-0000-0000-000000000091',
        taskId: TASK_PHOTO_FLOOR,
        taskListUnitId: TLU_DAILY,
        completedAt: '2026-06-24T08:45:00.000Z',
        // noon UTC — maps to June 24 in every timezone from UTC-11 to UTC+12
        logicalDayStart: '2026-06-24T12:00:00.000Z',
        status: 'completed' as const,
        // Inline SVG data URL — renders in the report modal without any server file.
        images: [{src: DEMO_FLOOR_IMG, path: 'reports/floor_june24.jpg'}],
        commentImages: [],
        completedBy: [CLIENT_USER_ID],
        comment: null,
        version: 1,
        createdAt: '2026-06-24T08:45:00.000Z',
        updatedAt: '2026-06-24T08:45:00.000Z',
        createdBy: CLIENT_USER_ID,
        updatedBy: CLIENT_USER_ID,
    },
    // The four rows below give the Recurring Tasks grid at least one example of
    // every cell status (completed / incomplete / skipped / overdue) on real past
    // days, so new cell variants can be visually compared against existing ones
    // instead of against an empty table.
    {
        __typename: 'TaskReport' as const,
        id: 'rpt002-0000-0000-0000-000000000092',
        taskId: TASK_CHECK_STOCK,
        taskListUnitId: TLU_INVENTORY,
        completedAt: '2026-07-01T09:15:00.000Z',
        logicalDayStart: '2026-07-01T12:00:00.000Z',
        status: 'completed' as const,
        images: [],
        commentImages: [],
        completedBy: [CLIENT_USER_ID],
        comment: null,
        version: 1,
        createdAt: '2026-07-01T09:15:00.000Z',
        updatedAt: '2026-07-01T09:15:00.000Z',
        createdBy: CLIENT_USER_ID,
        updatedBy: CLIENT_USER_ID,
    },
    {
        __typename: 'TaskReport' as const,
        id: 'rpt003-0000-0000-0000-000000000093',
        taskId: TASK_PULL_STOCK,
        taskListUnitId: TLU_INVENTORY,
        completedAt: '2026-07-01T17:40:00.000Z',
        logicalDayStart: '2026-07-01T12:00:00.000Z',
        status: 'incomplete' as const,
        images: [],
        commentImages: [],
        completedBy: [CLIENT_USER_ID],
        comment: 'Backroom delivery hadn\'t arrived yet — will pull and shelve first thing tomorrow.',
        version: 1,
        createdAt: '2026-07-01T17:40:00.000Z',
        updatedAt: '2026-07-01T17:40:00.000Z',
        createdBy: CLIENT_USER_ID,
        updatedBy: CLIENT_USER_ID,
    },
    {
        __typename: 'TaskReport' as const,
        id: 'rpt004-0000-0000-0000-000000000094',
        taskId: TASK_PHOTO_SHELVES,
        taskListUnitId: TLU_INVENTORY,
        completedAt: null,
        logicalDayStart: '2026-07-01T12:00:00.000Z',
        status: 'skipped' as const,
        images: [],
        commentImages: [],
        completedBy: [],
        comment: null,
        version: 1,
        createdAt: '2026-07-01T12:00:00.000Z',
        updatedAt: '2026-07-01T12:00:00.000Z',
        createdBy: CLIENT_USER_ID,
        updatedBy: CLIENT_USER_ID,
    },
    {
        __typename: 'TaskReport' as const,
        id: 'rpt005-0000-0000-0000-000000000095',
        taskId: TASK_CHECK_STOCK,
        taskListUnitId: TLU_INVENTORY,
        completedAt: null,
        logicalDayStart: '2026-07-02T12:00:00.000Z',
        // Not in the generated TaskReportStatus enum yet — matches the raw value
        // ScheduleCellIcon checks for (still-closeable, distinct from 'skipped').
        status: 'overdue' as const,
        images: [],
        commentImages: [],
        completedBy: [],
        comment: null,
        version: 1,
        createdAt: '2026-07-02T12:00:00.000Z',
        updatedAt: '2026-07-02T12:00:00.000Z',
        createdBy: CLIENT_USER_ID,
        updatedBy: CLIENT_USER_ID,
    },
    {
        __typename: 'TaskReport' as const,
        id: 'rpt006-0000-0000-0000-000000000096',
        taskId: TASK_PULL_STOCK,
        taskListUnitId: TLU_INVENTORY,
        // Submitted the next morning, after the deadline but inside the schedule's
        // "Allow late completion" grace window.
        completedAt: '2026-07-03T08:20:00.000Z',
        logicalDayStart: '2026-07-02T12:00:00.000Z',
        // Not in the generated TaskReportStatus enum yet — same raw-value pattern
        // as 'overdue' above.
        status: 'completed_late' as const,
        images: [],
        commentImages: [],
        completedBy: [CLIENT_USER_ID],
        comment: null,
        version: 1,
        createdAt: '2026-07-02T08:20:00.000Z',
        // Matches completedAt: the report was last touched when it was actually
        // submitted late, not when the placeholder row was first created.
        updatedAt: '2026-07-03T08:20:00.000Z',
        createdBy: CLIENT_USER_ID,
        updatedBy: CLIENT_USER_ID,
    },
    {
        __typename: 'TaskReport' as const,
        id: 'rpt009-0000-0000-0000-000000000099',
        taskId: TASK_PHOTO_SHELVES,
        taskListUnitId: TLU_INVENTORY,
        completedAt: '2026-07-02T11:05:00.000Z',
        logicalDayStart: '2026-07-02T12:00:00.000Z',
        status: 'completed' as const,
        images: [],
        commentImages: [],
        completedBy: [CLIENT_USER_ID],
        comment: null,
        version: 1,
        createdAt: '2026-07-02T11:05:00.000Z',
        updatedAt: '2026-07-02T11:05:00.000Z',
        createdBy: CLIENT_USER_ID,
        updatedBy: CLIENT_USER_ID,
    },
    // Jul 3 — every status on one day: on-time completion, a skip, and a still-
    // open overdue occurrence on a task other than "Check low-stock alerts",
    // so the row-level overdue marker isn't only ever seen on one row.
    {
        __typename: 'TaskReport' as const,
        id: 'rpt010-0000-0000-0000-00000000009a',
        taskId: TASK_CHECK_STOCK,
        taskListUnitId: TLU_INVENTORY,
        completedAt: '2026-07-03T09:10:00.000Z',
        logicalDayStart: '2026-07-03T12:00:00.000Z',
        status: 'completed' as const,
        images: [],
        commentImages: [],
        completedBy: [CLIENT_USER_ID],
        comment: null,
        version: 1,
        createdAt: '2026-07-03T09:10:00.000Z',
        updatedAt: '2026-07-03T09:10:00.000Z',
        createdBy: CLIENT_USER_ID,
        updatedBy: CLIENT_USER_ID,
    },
    {
        __typename: 'TaskReport' as const,
        id: 'rpt011-0000-0000-0000-00000000009b',
        taskId: TASK_PULL_STOCK,
        taskListUnitId: TLU_INVENTORY,
        completedAt: null,
        logicalDayStart: '2026-07-03T12:00:00.000Z',
        status: 'skipped' as const,
        images: [],
        commentImages: [],
        completedBy: [],
        comment: null,
        version: 1,
        createdAt: '2026-07-03T12:00:00.000Z',
        updatedAt: '2026-07-03T12:00:00.000Z',
        createdBy: CLIENT_USER_ID,
        updatedBy: CLIENT_USER_ID,
    },
    {
        __typename: 'TaskReport' as const,
        id: 'rpt012-0000-0000-0000-00000000009c',
        taskId: TASK_PHOTO_SHELVES,
        taskListUnitId: TLU_INVENTORY,
        completedAt: null,
        logicalDayStart: '2026-07-03T12:00:00.000Z',
        // Not in the generated TaskReportStatus enum yet — same raw-value pattern
        // as 'overdue' above.
        status: 'overdue' as const,
        images: [],
        commentImages: [],
        completedBy: [],
        comment: null,
        version: 1,
        createdAt: '2026-07-03T12:00:00.000Z',
        updatedAt: '2026-07-03T12:00:00.000Z',
        createdBy: CLIENT_USER_ID,
        updatedBy: CLIENT_USER_ID,
    },
    // Jul 4 — a completion closed two days late (bigger "Completed N d. late"
    // count than rpt006's one-day example), plus an on-time completion and an
    // incomplete with a comment, so the row-level statuses stay varied.
    {
        __typename: 'TaskReport' as const,
        id: 'rpt013-0000-0000-0000-00000000009d',
        taskId: TASK_CHECK_STOCK,
        taskListUnitId: TLU_INVENTORY,
        completedAt: '2026-07-06T10:30:00.000Z',
        logicalDayStart: '2026-07-04T12:00:00.000Z',
        status: 'completed_late' as const,
        images: [],
        commentImages: [],
        completedBy: [CLIENT_USER_ID],
        comment: null,
        version: 1,
        createdAt: '2026-07-04T12:00:00.000Z',
        updatedAt: '2026-07-06T10:30:00.000Z',
        createdBy: CLIENT_USER_ID,
        updatedBy: CLIENT_USER_ID,
    },
    {
        __typename: 'TaskReport' as const,
        id: 'rpt014-0000-0000-0000-00000000009e',
        taskId: TASK_PULL_STOCK,
        taskListUnitId: TLU_INVENTORY,
        completedAt: '2026-07-04T08:50:00.000Z',
        logicalDayStart: '2026-07-04T12:00:00.000Z',
        status: 'completed' as const,
        images: [],
        commentImages: [],
        completedBy: [CLIENT_USER_ID],
        comment: null,
        version: 1,
        createdAt: '2026-07-04T08:50:00.000Z',
        updatedAt: '2026-07-04T08:50:00.000Z',
        createdBy: CLIENT_USER_ID,
        updatedBy: CLIENT_USER_ID,
    },
    {
        __typename: 'TaskReport' as const,
        id: 'rpt015-0000-0000-0000-00000000009f',
        taskId: TASK_PHOTO_SHELVES,
        taskListUnitId: TLU_INVENTORY,
        completedAt: '2026-07-04T18:20:00.000Z',
        logicalDayStart: '2026-07-04T12:00:00.000Z',
        status: 'incomplete' as const,
        images: [],
        commentImages: [],
        completedBy: [CLIENT_USER_ID],
        comment: 'Lighting was too dim for a usable photo — will redo first thing tomorrow.',
        version: 1,
        createdAt: '2026-07-04T18:20:00.000Z',
        updatedAt: '2026-07-04T18:20:00.000Z',
        createdBy: CLIENT_USER_ID,
        updatedBy: CLIENT_USER_ID,
    },
    // Two extra past overdue occurrences for the same task, so the row-level
    // marker has a realistic "overdue more than once" example (count badge +
    // multi-row hover card), not just a single occurrence.
    {
        __typename: 'TaskReport' as const,
        id: 'rpt007-0000-0000-0000-000000000097',
        taskId: TASK_CHECK_STOCK,
        taskListUnitId: TLU_INVENTORY,
        completedAt: null,
        logicalDayStart: '2026-06-20T12:00:00.000Z',
        status: 'overdue' as const,
        images: [],
        commentImages: [],
        completedBy: [],
        comment: null,
        version: 1,
        createdAt: '2026-06-20T12:00:00.000Z',
        updatedAt: '2026-06-20T12:00:00.000Z',
        createdBy: CLIENT_USER_ID,
        updatedBy: CLIENT_USER_ID,
    },
    {
        __typename: 'TaskReport' as const,
        id: 'rpt008-0000-0000-0000-000000000098',
        taskId: TASK_CHECK_STOCK,
        taskListUnitId: TLU_INVENTORY,
        completedAt: null,
        logicalDayStart: '2026-06-27T12:00:00.000Z',
        status: 'overdue' as const,
        images: [],
        commentImages: [],
        completedBy: [],
        comment: null,
        version: 1,
        createdAt: '2026-06-27T12:00:00.000Z',
        updatedAt: '2026-06-27T12:00:00.000Z',
        createdBy: CLIENT_USER_ID,
        updatedBy: CLIENT_USER_ID,
    },
    // Overdue occurrence on a task that's also corrective, so the row shows both
    // the red overdue bar/icon and the amber corrective bar/icon at once.
    {
        __typename: 'TaskReport' as const,
        id: 'rpt016-0000-0000-0000-0000000000a0',
        taskId: TASK_ENTRANCE_SIGN,
        taskListUnitId: TLU_DAILY,
        completedAt: null,
        logicalDayStart: '2026-07-02T12:00:00.000Z',
        status: 'overdue' as const,
        images: [],
        commentImages: [],
        completedBy: [],
        comment: null,
        version: 1,
        createdAt: '2026-07-02T12:00:00.000Z',
        updatedAt: '2026-07-02T12:00:00.000Z',
        createdBy: CLIENT_USER_ID,
        updatedBy: CLIENT_USER_ID,
    },
];

// Restricts "Weekly Shelf Audit" to the Store Manager role, so the demo shows the
// Accessibility/roles feature (key icon, filter) working out of the box.
export const rolePermissions = [
    {
        id: 'rp-0001-0000-0000-0000-000000000001',
        ownerId: TENANT_ID,
        roleId: ROLE_ID_STORE_MANAGER,
        workspaceId: WORKSPACE_ID,
        objectType: 'task_list',
        objectId: TL_SHELVES,
        createdAt: NOW,
        createdBy: CLIENT_USER_ID,
    },
];
